/**
 * Geometry toolkit for palace room types.
 *
 * Room-type builders receive these helpers via the `T` argument to build()
 * (see docs/palace-room-types.md) rather than importing them, so every type
 * speaks the same dialect: axis-aligned boxes merged per material, doors cut
 * from `node.doors`, and a collider primitive for every solid the visitor
 * could walk into.
 *
 * Collider primitives (all 2D in plan, `top` = y where the solid stops):
 *   { rect: [minX, minZ, maxX, maxZ], top }
 *   { seg: [ax, az, bx, bz], t: halfThickness, top }   — angled walls
 *   { circle: [cx, cz, r], top }                        — columns, fountains
 *   { ring: [cx, cz, r], gaps: [[a0, a1], ...], top }   — stay-inside drums,
 *     gaps in radians using atan2(dx, dz) (0 faces +z), a0 < a1. Only binds a
 *     visitor standing inside the owning node's footprint (see PalaceMuseum).
 */

import * as THREE from 'three';
import { mergeGeometries } from 'three/examples/jsm/utils/BufferGeometryUtils.js';
import { PALACE } from './constants';

export const BASE_H = 0.18; // baseboard height
export const BASE_P = 0.045; // baseboard proudness off the wall face
export const COR_H = 0.4; // cornice height
export const COR_P = 0.065; // cornice proudness
export const RAIL_H = 0.09; // picture rail height, sitting on PALACE.RAIL
export const RAIL_P = 0.075; // ... and how far it stands off the wall face
export const CASE_W = 0.14; // architrave casing width around a door
export const CASE_P = 0.05; // casing proudness beyond the wall face
export const CASE_LINTEL = 0.16; // lintel band height above the door
const CASE_IN = 0.03; // casing wrap into the door reveal — this overlap is
// what keeps the casing faces off the wall's reveal plane (no z-fighting)

// An axis-aligned box spanning [x1,x2]×[y1,y2]×[z1,z2]; spans may be unordered.
export const box = (x1, x2, y1, y2, z1, z2) => {
  const g = new THREE.BoxGeometry(Math.abs(x2 - x1), Math.abs(y2 - y1), Math.abs(z2 - z1));
  g.translate((x1 + x2) / 2, (y1 + y2) / 2, (z1 + z2) / 2);
  return g;
};

// A wall along an arbitrary 2D segment (for chamfers/octagons). Returns the
// geometry and its collider.
export const angledWall = (ax, az, bx, bz, height, t = PALACE.WALL_T, y0 = 0) => {
  const len = Math.hypot(bx - ax, bz - az);
  const g = new THREE.BoxGeometry(len, height - y0, t);
  g.rotateY(Math.atan2(-(bz - az), bx - ax));
  g.translate((ax + bx) / 2, (y0 + height) / 2, (az + bz) / 2);
  return { geo: g, collider: { seg: [ax, az, bx, bz], t: t / 2, top: height } };
};

export const mergedMesh = (geos, material) => {
  const merged = mergeGeometries(geos, false);
  geos.forEach((g) => g.dispose());
  const mesh = new THREE.Mesh(merged, material);
  mesh.matrixAutoUpdate = false;
  mesh.updateMatrix();
  return mesh;
};

const doorsOn = (node, side) =>
  (node.doors || []).filter((d) => d.side === side).sort((a, b) => a.at - b.at);

// One of the node's four perimeter walls, with every door on that side cut
// out (solid runs + lintels). Walls sit just inside the footprint; N/S walls
// take the corners, E/W walls stop short so corner boxes never overlap.
export const wallForSide = (node, side, height = node.height) => {
  const { WALL_T, DOOR_H } = PALACE;
  const geos = [];
  const colliders = [];
  const doors = doorsOn(node, side);

  const horizontal = side === 'N' || side === 'S'; // wall runs along x
  const f1 = side === 'N' ? node.z2 - WALL_T : side === 'S' ? node.z1
    : side === 'E' ? node.x2 - WALL_T : node.x1;
  const f2 = f1 + WALL_T;
  const a1 = horizontal ? node.x1 : node.z1 + WALL_T;
  const a2 = horizontal ? node.x2 : node.z2 - WALL_T;

  const piece = (r1, r2, y0, y1) => {
    if (r2 - r1 < 0.01) return;
    geos.push(horizontal ? box(r1, r2, y0, y1, f1, f2) : box(f1, f2, y0, y1, r1, r2));
    if (y0 === 0) {
      colliders.push({
        rect: horizontal ? [r1, f1, r2, f2] : [f1, r1, f2, r2],
        top: y1,
      });
    }
  };

  let cursor = a1;
  for (const door of doors) {
    const dw = door.w || PALACE.DOOR_W;
    const dh = door.h || DOOR_H;
    piece(cursor, door.at - dw / 2, 0, height);
    piece(door.at - dw / 2, door.at + dw / 2, dh, height); // lintel
    cursor = door.at + dw / 2;
  }
  piece(cursor, a2, 0, height);
  return { geos, colliders };
};

// Marble architrave wrapping each door through this node's own wall: two
// jambs with shoulders and a lintel band, proud of the interior face and
// wrapped CASE_IN into the reveal so no casing face is coplanar with the
// wall's cut faces. The neighbouring room cases its own half.
export const casingsForNode = (node) => {
  const { WALL_T, DOOR_H } = PALACE;
  const geos = [];
  for (const door of node.doors || []) {
    const dw = door.w || PALACE.DOOR_W;
    const dh = door.h || DOOR_H;
    const horizontal = door.side === 'N' || door.side === 'S';
    const fIn = door.side === 'N' ? node.z2 - WALL_T - CASE_P
      : door.side === 'S' ? node.z1 + WALL_T + CASE_P
        : door.side === 'E' ? node.x2 - WALL_T - CASE_P : node.x1 + WALL_T + CASE_P;
    const fOut = door.side === 'N' ? node.z2 : door.side === 'S' ? node.z1
      : door.side === 'E' ? node.x2 : node.x1;
    const p = (r1, r2, y0, y1) =>
      horizontal ? box(r1, r2, y0, y1, fIn, fOut) : box(fIn, fOut, y0, y1, r1, r2);
    geos.push(
      p(door.at - dw / 2 - CASE_W, door.at - dw / 2 + CASE_IN, 0, dh + CASE_LINTEL),
      p(door.at + dw / 2 - CASE_IN, door.at + dw / 2 + CASE_W, 0, dh + CASE_LINTEL),
      p(door.at - dw / 2 + CASE_IN, door.at + dw / 2 - CASE_IN, dh - CASE_IN, dh + CASE_LINTEL)
    );
  }
  return geos;
};

// Baseboards, picture rail and cornices on the four interior faces. Baseboards
// and rail gap where a door (plus its casing) lands — the rail sits below the
// door head — while cornices ride above the doors. E/W runs stop short of the
// corners so no two trim boxes share a face.
export const baseAndCornice = (node, height = node.height, corniceH = COR_H) => {
  const { WALL_T } = PALACE;
  const xI1 = node.x1 + WALL_T;
  const xI2 = node.x2 - WALL_T;
  const zI1 = node.z1 + WALL_T;
  const zI2 = node.z2 - WALL_T;
  const geos = [];

  const faces = [
    { side: 'N', horizontal: true, face: zI2, sign: -1, a1: xI1, a2: xI2 },
    { side: 'S', horizontal: true, face: zI1, sign: 1, a1: xI1, a2: xI2 },
    { side: 'E', horizontal: false, face: xI2, sign: -1, a1: zI1 + BASE_P, a2: zI2 - BASE_P },
    { side: 'W', horizontal: false, face: xI1, sign: 1, a1: zI1 + BASE_P, a2: zI2 - BASE_P },
  ];
  for (const f of faces) {
    const bF1 = f.face;
    const bF2 = f.face + f.sign * BASE_P;
    const rF2 = f.face + f.sign * RAIL_P;
    const run = (r1, r2) => {
      if (r2 - r1 < 0.02) return;
      geos.push(
        f.horizontal ? box(r1, r2, 0, BASE_H, bF1, bF2) : box(bF1, bF2, 0, BASE_H, r1, r2),
        // The rail the hang is measured against: PALACE.RAIL is the ceiling of
        // every segment, so the moulding is where the pictures stop.
        f.horizontal
          ? box(r1, r2, PALACE.RAIL, PALACE.RAIL + RAIL_H, bF1, rF2)
          : box(bF1, rF2, PALACE.RAIL, PALACE.RAIL + RAIL_H, r1, r2)
      );
    };
    let cursor = f.a1;
    for (const door of doorsOn(node, f.side)) {
      const dw = (door.w || PALACE.DOOR_W) / 2 + CASE_W;
      run(cursor, door.at - dw);
      cursor = door.at + dw;
    }
    run(cursor, f.a2);

    const cF2 = f.face + f.sign * COR_P;
    const c1 = f.horizontal ? f.a1 : zI1 + COR_P;
    const c2 = f.horizontal ? f.a2 : zI2 - COR_P;
    geos.push(
      f.horizontal
        ? box(c1, c2, height - corniceH, height, bF1, cF2)
        : box(bF1, cF2, height - corniceH, height, c1, c2)
    );
  }
  return geos;
};

// Coffered emissive laylight: beam border plus glowing plane, centred on the
// interior. Returns trim geometries (merge into trim) and the light mesh.
export const laylightCoffer = (node, kit, layW, layD, y = node.height) => {
  const BEAM = 0.14;
  const cx = (node.x1 + node.x2) / 2;
  const cz = (node.z1 + node.z2) / 2;
  const lx1 = cx - layW / 2;
  const lx2 = cx + layW / 2;
  const lz1 = cz - layD / 2;
  const lz2 = cz + layD / 2;
  const trimGeos = [
    box(lx1 - BEAM, lx2 + BEAM, y - 0.15, y, lz1 - BEAM, lz1),
    box(lx1 - BEAM, lx2 + BEAM, y - 0.15, y, lz2, lz2 + BEAM),
    box(lx1 - BEAM, lx1, y - 0.15, y, lz1, lz2),
    box(lx2, lx2 + BEAM, y - 0.15, y, lz1, lz2),
  ];
  const mesh = new THREE.Mesh(kit.geometries.unitPlane, kit.materials.laylight);
  mesh.rotation.x = Math.PI / 2;
  mesh.scale.set(layW, layD, 1);
  mesh.position.set(cx, y - 0.02, cz);
  mesh.matrixAutoUpdate = false;
  mesh.updateMatrix();
  return { trimGeos, mesh };
};

// A wooden viewing bench with its collider. Axis-aligned: rotY 0 runs along
// x, Math.PI/2 along z.
export const bench = (kit, x, z, rotY = 0, len = 2.0) => {
  const mesh = new THREE.Mesh(kit.geometries.unitBox, kit.materials.benchWood);
  const along = rotY ? 0.55 : len;
  const across = rotY ? len : 0.55;
  mesh.scale.set(along, 0.46, across);
  mesh.position.set(x, 0.23, z);
  mesh.matrixAutoUpdate = false;
  mesh.updateMatrix();
  return {
    mesh,
    // Soft: a bench is scenery, not an obstacle — you walk through it rather
    // than get herded around it in a room you're trying to read the walls of.
    collider: {
      rect: [x - along / 2, z - across / 2, x + along / 2, z + across / 2],
      top: 0.5,
      soft: true,
    },
  };
};

// A cylindrical drum wall with door gaps, for domed/circular rooms inside a
// rectangular footprint. Cuts an angular gap per door, adds a lintel arc over
// it, and runs a short vestibule (two jamb walls + soffit) from the drum out
// to the footprint edge so the door still reads as a framed opening.
// Angles use atan2(dx, dz): 0 faces +z (three.js cylinder convention).
// Returns { curvedGeos, boxGeos, colliders }. Curved geometry must be drawn
// with a DoubleSide material (kit.materials.dome is).
export const drumWithDoors = (node, r, height, opts = {}) => {
  const { DOOR_H } = PALACE;
  const t = opts.tunnelT ?? 0.25;
  const cx = (node.x1 + node.x2) / 2;
  const cz = (node.z1 + node.z2) / 2;
  const curvedGeos = [];
  const boxGeos = [];
  const colliders = [];
  const gaps = [];

  const doors = (node.doors || []).map((door) => {
    const dw = door.w || PALACE.DOOR_W;
    const dh = door.h || DOOR_H;
    const px = door.side === 'E' ? node.x2 : door.side === 'W' ? node.x1 : door.at;
    const pz = door.side === 'N' ? node.z2 : door.side === 'S' ? node.z1 : door.at;
    const theta = Math.atan2(px - cx, pz - cz);
    const half = Math.asin(Math.min(0.9, (dw / 2 + t) / r));
    gaps.push([theta - half, theta + half]);
    return { door, dw, dh, px, pz, theta, half };
  });

  // Solid arcs between the gaps.
  const sorted = [...doors].sort((a, b) => a.theta - b.theta);
  const arc = (a0, a1, y0, y1) => {
    if (a1 - a0 < 0.01) return;
    const g = new THREE.CylinderGeometry(r, r, y1 - y0, 48, 1, true, a0, a1 - a0);
    g.translate(cx, (y0 + y1) / 2, cz);
    curvedGeos.push(g);
  };
  if (!sorted.length) {
    arc(0, Math.PI * 2, 0, height);
  } else {
    for (let i = 0; i < sorted.length; i++) {
      const a0 = sorted[i].theta + sorted[i].half;
      const a1 = i + 1 < sorted.length ? sorted[i + 1].theta - sorted[i + 1].half
        : sorted[0].theta - sorted[0].half + Math.PI * 2;
      arc(a0, a1, 0, height);
      arc(sorted[i].theta - sorted[i].half, sorted[i].theta + sorted[i].half, sorted[i].dh, height);
    }
  }
  colliders.push({ ring: [cx, cz, r], gaps, top: height });

  // Vestibules from the drum face out to the footprint boundary.
  for (const { door, dw, dh } of doors) {
    const horizontal = door.side === 'N' || door.side === 'S';
    const edge = door.side === 'N' ? node.z2 : door.side === 'S' ? node.z1
      : door.side === 'E' ? node.x2 : node.x1;
    // The tunnel starts at the drum's depth under its *outermost* face, not
    // under the door centre: the drum curves away across the opening, so jambs
    // cut to the centre depth stop short of the arc they are meant to meet and
    // leave a slot of daylight down each side of the doorway (and over it).
    // Half the gap's chord, plus the 0.1 overlap, buries the seam in the arc.
    const lat = Math.abs(door.at - (horizontal ? cx : cz)) + dw / 2 + t;
    const depth = Math.sqrt(Math.max(0, r * r - lat * lat)) - 0.1;
    const inner = door.side === 'N' ? cz + depth : door.side === 'S' ? cz - depth
      : door.side === 'E' ? cx + depth : cx - depth;
    const lo = Math.min(edge, inner);
    const hi = Math.max(edge, inner);
    if (hi - lo < 0.05) continue;
    const jamb = (rA, rB) => {
      boxGeos.push(horizontal ? box(rA, rB, 0, height, lo, hi) : box(lo, hi, 0, height, rA, rB));
      colliders.push({
        rect: horizontal ? [Math.min(rA, rB), lo, Math.max(rA, rB), hi]
          : [lo, Math.min(rA, rB), hi, Math.max(rA, rB)],
        top: height,
      });
    };
    jamb(door.at - dw / 2 - t, door.at - dw / 2);
    jamb(door.at + dw / 2, door.at + dw / 2 + t);
    // soffit over the vestibule
    boxGeos.push(horizontal
      ? box(door.at - dw / 2, door.at + dw / 2, dh, height, lo, hi)
      : box(lo, hi, dh, height, door.at - dw / 2, door.at + dw / 2));
  }

  return { curvedGeos, boxGeos, colliders };
};

/**
 * The whole standard rectangular room in one call: floor and ceiling slabs,
 * four walls with doors cut, baseboards + cornice, marble door casings, and
 * an optional coffered laylight. Returns { group, colliders }.
 *
 * opts: {
 *   wallMaterial   — defaults to the wing's salon color (kit.wallFor(palette))
 *   floorMaterial  — defaults kit.materials.floor
 *   laylight       — true | false | { w, d } (default true, auto-inset)
 *   corniceH, height
 * }
 */
export const standardShell = (node, kit, opts = {}) => {
  const { WALL_T } = PALACE;
  const height = opts.height ?? node.height;
  const group = new THREE.Group();
  const colliders = [];

  const wallMaterial = opts.wallMaterial || kit.wallFor(node.palette || 0);
  const wallGeos = [];
  for (const side of ['N', 'S', 'E', 'W']) {
    const { geos, colliders: c } = wallForSide(node, side, height);
    wallGeos.push(...geos);
    colliders.push(...c);
  }
  group.add(mergedMesh(wallGeos, wallMaterial));

  group.add(mergedMesh(
    [box(node.x1, node.x2, -0.25, 0, node.z1, node.z2)],
    opts.floorMaterial || kit.materials.floor
  ));
  group.add(mergedMesh(
    [box(node.x1, node.x2, height, height + 0.25, node.z1, node.z2)],
    kit.materials.ceiling
  ));

  const trimGeos = baseAndCornice(node, height, opts.corniceH ?? COR_H);
  if (opts.laylight !== false) {
    const layW = opts.laylight?.w ?? Math.max(2, node.x2 - node.x1 - 2 * WALL_T - 4);
    const layD = opts.laylight?.d ?? Math.max(2, node.z2 - node.z1 - 2 * WALL_T - 4);
    const { trimGeos: beams, mesh } = laylightCoffer(node, kit, layW, layD, height);
    trimGeos.push(...beams);
    group.add(mesh);
  }
  group.add(mergedMesh(trimGeos, kit.materials.trim));

  const casings = casingsForNode(node);
  if (casings.length) group.add(mergedMesh(casings, kit.materials.marble));

  return { group, colliders };
};
